// React
import { useCallback, useMemo, useRef } from 'react';

// UI primitives
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';

// Form seam (GRAIN-2551)
import { Form } from '@components/Form';
import type { FormApi } from '@components/Form/FormProvider';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useTranslation } from '@hooks/useTranslation';
import { useCreateEvent } from '@hooks/events';
import { usePostHog } from '@posthog/react';
import { buildEventEditPath } from '@/react/utils/eventEditNavigation';

// API + schema
import { mapFormStateToPayload, type CreateEventsResult } from '@api/events/mutations';
import { makeCreateEventSchema, isStartedEvent, type CreateEventFormData } from '@/react/schemas/createEvent.schema';

// Local components
import {
  CreateEventForm,
  CreateEventActionsProvider,
  CreateEventFooter,
  createEmptyEvent,
  type EventFormState,
} from '../CreateEventForm/CreateEventForm';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

const DIALOG_HEIGHT = 'h-[90vh]';

/* -------------------------------------------------------------------------- */
/*  CreateEventDialog                                                          */
/* -------------------------------------------------------------------------- */

interface CreateEventDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  orderId: number;
  initialEvents?: EventFormState[];
  /** Called when the created events should render collapsed on the
   *  originating surface (multi-create or any-error path — the single,
   *  no-error path redirects to Edit instead and never calls this). */
  onEventsCreated?: (createdIds: number[]) => void;
}

export function CreateEventDialog({ isOpen, onOpenChange, orderId, initialEvents, onEventsCreated }: CreateEventDialogProps) {
  const { t } = useTranslation();
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        aria-label={t('create_event.title')}
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={handleClose}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('[data-radix-popper-content-wrapper]')) {
            e.preventDefault();
          }
        }}
        className={cn(
          'max-w-dialog rounded-input border-dinnerplate-white px-0 pb-0 bg-body outline-none overflow-hidden',
          '[&>button:last-child]:hidden',
          DIALOG_HEIGHT,
        )}
      >
        {/* Remount on each open so the form resets to fresh defaultValues. */}
        {isOpen && (
          <CreateEventDialogContent
            orderId={orderId}
            initialEvents={initialEvents}
            onClose={handleClose}
            onOpenChange={onOpenChange}
            onEventsCreated={onEventsCreated}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  CreateEventDialogContent — holds the <Form> seam                           */
/* -------------------------------------------------------------------------- */

interface CreateEventDialogContentProps {
  orderId: number;
  initialEvents?: EventFormState[];
  onClose: () => void;
  onOpenChange: (isOpen: boolean) => void;
  onEventsCreated?: (createdIds: number[]) => void;
}

function CreateEventDialogContent({ orderId, initialEvents, onClose, onOpenChange, onEventsCreated }: CreateEventDialogContentProps) {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const formApiRef = useRef<FormApi | null>(null);

  const schema = useMemo(
    () =>
      makeCreateEventSchema({
        formatRequired: t('create_event.errors.format'),
        productLineRequired: t('create_event.errors.product_line'),
        guestCountRequired: t('create_event.errors.guest_count'),
        servingLayoutRequired: t('create_event.errors.serving_layout'),
        servingTimeRequired: t('create_event.errors.serving_time'),
        servingDateRequired: t('create_event.errors.serving_date'),
      }),
    [t],
  );

  const handleSuccess = useCallback((result: CreateEventsResult) => {
    if (result.created.length === 1 && result.errors.length === 0) {
      const path = buildEventEditPath(orderId, result.created[0].id);
      if (window.Turbo) {
        window.Turbo.visit(path);
      } else {
        window.location.assign(path);
      }
      return;
    }

    onOpenChange(false);
    onEventsCreated?.(result.created.map((event) => event.id));
  }, [orderId, onOpenChange, onEventsCreated]);

  const mutation = useCreateEvent({ onSuccess: handleSuccess });

  // The Sheet's react-remove-scroll blocks wheel events on portaled siblings.
  // Manually forward wheel deltas to the scroll container as a workaround.
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop += e.deltaY;
  }, []);

  const handleSubmit = useCallback(
    (values: CreateEventFormData) => {
      const startedEvents = values.events.filter(isStartedEvent);
      if (startedEvents.length === 0) return;
      posthog.capture('event_created', { order_id: orderId, events_count: startedEvents.length });
      mutation.mutate({ orderId, events: startedEvents.map(mapFormStateToPayload) });
    },
    [orderId, posthog, mutation],
  );

  return (
    <Form<CreateEventFormData>
      formRef={formApiRef}
      schema={schema}
      defaultValues={{ events: initialEvents ?? [createEmptyEvent(1)] }}
      onSubmit={handleSubmit}
      isPending={mutation.isPending}
      className="flex flex-1 flex-col min-h-0"
    >
      <CreateEventActionsProvider scrollRef={scrollRef}>
        <ModalLayout
          as="div"
          onWheel={handleWheel}
          title={<DialogTitle asChild><ModalTitle>{t('create_event.title')}</ModalTitle></DialogTitle>}
          onClose={onClose}
          scrollRef={scrollRef}
          footer={<CreateEventFooter isPending={mutation.isPending} />}
        >
          <CreateEventForm />
        </ModalLayout>
      </CreateEventActionsProvider>
    </Form>
  );
}
